/**
 * Inline link rendering for the legal disclaimer — RN port of the web
 * widget's `lib/text-helpers.tsx`.
 *
 * Same matching rules (word-boundary, escaped, one pass per configured link)
 * so the same disclaimer config highlights the same words on both platforms.
 * Only the leaf differs: `<a href>` becomes a `<Text onPress>` that hands the
 * URL to `Linking`, which is RN's "open in the system browser" — the closest
 * equivalent of `target="_blank"`.
 */

import { Fragment, type ReactNode } from "react";
import { Linking, Text, type TextStyle } from "react-native";

/** Tailwind's `text-blue-500`, the colour the web disclaimer links use. */
const LINK_COLOR = "#3b82f6";

const escapeForRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const openUrl = (url: string): void => {
  // Best-effort: a malformed or unhandled scheme must not crash the widget.
  void Linking.openURL(url).catch(() => {});
};

/**
 * Splits `text` on each configured link word and returns a node list ready to
 * drop inside a `<Text>`. Callers own the surrounding typography; only the
 * link colour is applied here.
 */
export const renderTextWithLinks = (text: string, links?: Record<string, string>, linkStyle?: TextStyle): ReactNode => {
  if (!links) return text;

  let parts: ReactNode[] = [text];

  Object.entries(links).forEach(([word, url]) => {
    parts = parts.flatMap((part, partIndex) => {
      if (typeof part !== "string") return part;

      const pattern = new RegExp(`\\b(${escapeForRegExp(word)})\\b`, "g");

      return part.split(pattern).map((segment, segmentIndex) =>
        segment === word ? (
          <Text key={`${partIndex}-${segmentIndex}`} style={[{ color: LINK_COLOR }, linkStyle]} onPress={() => openUrl(url)}>
            {word}
          </Text>
        ) : (
          <Fragment key={`${partIndex}-${segmentIndex}`}>{segment}</Fragment>
        ),
      );
    });
  });

  return parts;
};
